import { useCallback, useEffect, useState, useMemo } from "react";
import { useLocalization } from "@fluent/react";
import { Button, LoadingSkeleton, MenuButton, Row } from "@narmi/design_system";
import {
  ContentCard,
  useLoadingContext,
  useNotificationContext,
} from "cerulean";
import Account from "byzantine/src/Account";
import MemberFriend from "byzantine/src/MemberFriend";
import type { InstitutionFeatures } from "byzantine/src/types";
import { useUserFeatures } from "../../../components/contexts/UserFeaturesContext";
import DeleteDialog from "./DeleteDialog";
import NewMemberModal from "../../transfer/NewMemberModal";

interface ManageFriendPopoverProps {
  openDeleteDialog: () => void;
}

const ManageFriendPopover = ({
  openDeleteDialog,
}: ManageFriendPopoverProps) => (
  <MenuButton triggerIcon="more-vertical">
    <MenuButton.Item
      label="Delete"
      startIcon="trash-2"
      onSelect={openDeleteDialog}
    />
  </MenuButton>
);

interface MemberFriendCardProps {
  memberFriend: MemberFriend;
  fetchMemberFriends: () => Promise<void>;
}

const MemberFriendCard = ({
  memberFriend,
  fetchMemberFriends,
}: MemberFriendCardProps) => {
  const [isDeleteMemberDialogOpen, setIsDeleteMemberDialogOpen] =
    useState(false);
  const { sendNotification } = useNotificationContext();
  const { setIsLoading } = useLoadingContext();
  const { l10n } = useLocalization();

  const deleteMember = async () => {
    setIsDeleteMemberDialogOpen(false);
    setIsLoading(true);
    try {
      await memberFriend.delete();
      setIsLoading(false);
      sendNotification({
        type: "success",
        text: l10n.getString("member-deleted"),
      });
      await fetchMemberFriends();
    } catch {
      sendNotification({
        type: "negative",
        text: l10n.getString("member-not-deleted"),
      });
      setIsLoading(false);
    }
  };

  return (
    <>
      <ContentCard>
        <Row>
          <Row.Item>
            <div className="fontColor--heading fontWeight--semibold">
              {memberFriend.name}
            </div>
          </Row.Item>
          <Row.Item shrink>
            <div style={{ marginTop: "-4px" }}>
              <ManageFriendPopover
                openDeleteDialog={() => setIsDeleteMemberDialogOpen(true)}
              />
            </div>
          </Row.Item>
        </Row>
        <div className="margin--top--xxs">
          {memberFriend.displayMaskedAccountNumber()}
        </div>
      </ContentCard>
      <DeleteDialog
        recipientName={memberFriend.name}
        recipientType={l10n.getString("member-or-friend")}
        onDelete={deleteMember}
        isOpen={isDeleteMemberDialogOpen}
        closeDialog={() => setIsDeleteMemberDialogOpen(false)}
      />
    </>
  );
};

const MemberFriendSection = ({ accounts }: { accounts: Account[] }) => {
  const [memberFriends, setMemberFriends] = useState<MemberFriend[]>([]);
  const [isAddMemberDialogOpen, setIsAddMemberDialogOpen] = useState(false);
  const [isFetchingMembers, setIsFetchingMembers] = useState(false);
  const { l10n } = useLocalization();
  const features = useUserFeatures() as InstitutionFeatures;

  const { sendNotification } = useNotificationContext();
  const fetchMemberFriends = async () => {
    setIsFetchingMembers(true);
    try {
      setMemberFriends((await MemberFriend.getMemberFriends()) || []);
    } catch {
      sendNotification({
        type: "negative",
        text: l10n.getString("member-fetch-fail"),
      });
    } finally {
      setIsFetchingMembers(false);
    }
  };

  const showAddMemberDialog = useCallback(() => {
    setIsAddMemberDialogOpen(true);
  }, []);

  const hideAddMemberDialog = useCallback(() => {
    setIsAddMemberDialogOpen(false);
  }, []);

  const onAddMember = useCallback(
    (id: string, error?: string) => {
      if (!error) {
        sendNotification({
          type: "success",
          text: l10n.getString("member-added"),
        });
        fetchMemberFriends();
      } else {
        sendNotification({
          type: "negative",
          text: error,
        });
      }
    },
    [l10n, fetchMemberFriends, sendNotification]
  );

  useEffect(() => {
    fetchMemberFriends();
  }, []);

  const validTransferAccounts = useMemo(
    () => accounts.filter((a) => a.isInternal() && a.isValidM2mSource()),
    [accounts]
  );

  return (
    <>
      <div className="margin--top--xl margin--bottom--l">
        <Row>
          <Row.Item>
            <div className="fontSize--l fontColor--heading fontWeight--bold">
              {l10n.getString("members-label")}
            </div>
          </Row.Item>
          {!!features.olb_allow_add_member && (
            <Row.Item shrink>
              <Button
                kind="plain"
                onClick={showAddMemberDialog}
                label={l10n.getString("member-add-label")}
              />
            </Row.Item>
          )}
        </Row>
      </div>
      <LoadingSkeleton content="paragraph" isLoading={isFetchingMembers}>
        <div
          className={
            memberFriends?.length ? "recipients-container" : "no-recipients"
          }
        >
          {memberFriends?.map((member) => (
            <MemberFriendCard
              key={member.id}
              memberFriend={member}
              fetchMemberFriends={fetchMemberFriends}
            />
          ))}
          {!memberFriends?.length && (
            <div>{l10n.getString("member-empty")}</div>
          )}
        </div>
      </LoadingSkeleton>
      <NewMemberModal
        accounts={validTransferAccounts}
        newMemberAdded={onAddMember}
        open={isAddMemberDialogOpen}
        handleClose={hideAddMemberDialog}
      />
    </>
  );
};

export default MemberFriendSection;
